import {
  ContactsOutlined,
  EditOutlined,
  ProjectOutlined,
  SaveOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { ProjectStatus } from "../../enum/common";
import {
  IProductionProject,
  IUpdateProductionProject,
} from "../../models/Production";
import { IContact, ICustomer } from "../../models/Sale";
import { useUpdateProductionProject } from "../../query/ProductionQuery";
import { useGetContact, useGetCustomer } from "../../query/SaleQuery";
import { notify } from "../../utils/toast";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ModalEditProductionProjectProps {
  open: boolean;
  onCancel: () => void;
  project: IProductionProject | null;
}

const ModalEditProductionProject = ({
  open,
  onCancel,
  project,
}: ModalEditProductionProjectProps) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const updateProjectMutation = useUpdateProductionProject();
  const [customerSearch, setCustomerSearch] = React.useState<string>("");
  const [customerPage, setCustomerPage] = React.useState<number>(1);
  const [contactPage, setContactPage] = React.useState<number>(1);
  const [contactSearch, setContactSearch] = React.useState<string>("");
  useEffect(() => {
    if (open && project) {
      let parsedContacts: string[] = [];
      try {
        const contacts = JSON.parse(project.contactProject);
        parsedContacts = contacts.map(
          (c: any) => `${c.name} - ${c.phone} - ${c.position}`,
        );
      } catch {
        parsedContacts = [project.contactProject];
      }

      form.setFieldsValue({
        projectName: project.projectName,
        project: project.project,
        projectDescription: project.projectDescription,
        customerName: project.customerName,
        contactProject: parsedContacts,
        requestFrom: project.requestFrom,
        saleInCharge: project.saleInCharge,
        saleAdInCharge: project.saleAdInCharge,
        purchaseInCharge: project.purchaseInCharge,
        requestDate: dayjs(project.requestDate),
        startDate: dayjs(project.startDate),
        deadline: dayjs(project.deadline),
        status: project.status,
      });
    }
    // eslint-disable-next-line
  }, [open]);
  const { data: customerData, isLoading: isLoadingCustomer } = useGetCustomer({
    search: customerSearch,
    page: customerPage,
    limit: 5,
  });
  const { data: contactData, isLoading: isLoadingContact } = useGetContact({
    search: contactSearch,
    page: contactPage,
    limit: 5,
  });

  const [allCustomers, setAllCustomers] = React.useState<ICustomer[]>([]);
  const [allContacts, setAllContacts] = React.useState<IContact[]>([]);

  React.useEffect(() => {
    if (customerData?.data !== undefined) {
      if (customerPage === 1) {
        setAllCustomers(customerData.data);
      } else {
        setAllCustomers((prev) => [...prev, ...customerData.data]);
      }
    }
  }, [customerData, customerPage]);

  React.useEffect(() => {
    setCustomerPage(1);
  }, [customerSearch]);

  React.useEffect(() => {
    if (contactData?.data !== undefined) {
      if (contactPage === 1) {
        setAllContacts(contactData.data);
      } else {
        setAllContacts((prev) => [...prev, ...contactData.data]);
      }
    }
  }, [contactData, contactPage]);

  React.useEffect(() => {
    setContactPage(1);
  }, [contactSearch]);

  const handleCustomerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 10;

    if (
      isNearBottom &&
      !isLoadingCustomer &&
      customerData?.pagination &&
      customerPage < customerData.pagination.totalPages
    ) {
      setCustomerPage((prev) => prev + 1);
    }
  };

  const handleContactScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 10;

    if (
      isNearBottom &&
      !isLoadingContact &&
      contactData?.pagination &&
      contactPage < contactData.pagination.totalPages
    ) {
      setContactPage((prev) => prev + 1);
    }
  };
  const debouncedCustomerSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCustomerSearch(value || "");
      }, 300),
    [],
  );

  const debouncedContactSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setContactSearch(value || "");
      }, 300),
    [],
  );
  const steps = [
    {
      title: "Thông tin dự án",
      icon: <ProjectOutlined />,
    },
    {
      title: "Khách hàng",
      icon: <ContactsOutlined />,
    },
    {
      title: "Đội ngũ & Thời gian",
      icon: <TeamOutlined />,
    },
  ];

  const getStepFields = (step: number) => {
    switch (step) {
      case 0:
        return ["projectName", "project", "projectDescription"];
      case 1:
        return ["customerName", "contactProject", "requestFrom"];
      case 2:
        return ["requestDate", "startDate", "deadline"];
      default:
        return [];
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields(getStepFields(currentStep));
      setCurrentStep((prev) => prev + 1);
    } catch {}
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const contactList =
        values.contactProject?.map((contact: string) => {
          const [name, phone, position] = contact.split(" - ");
          return { name, phone, position };
        }) || [];

      const updateData: IUpdateProductionProject = {
        ...values,
        contactProject: JSON.stringify(contactList),
        requestDate: values.requestDate.format("YYYY-MM-DD"),
        startDate: values.startDate.format("YYYY-MM-DD"),
        deadline: values.deadline.format("YYYY-MM-DD"),
      };

      await updateProjectMutation.mutateAsync({
        id: project!.id,
        data: updateData,
      });

      notify.success("Cập nhật dự án thành công");

      onCancel();
      form.resetFields();
      setCurrentStep(0);
    } catch {
      notify.error("Có lỗi xảy ra khi cập nhật dự án");
    }
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
    setCurrentStep(0);
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ margin: 0 }}>
            <EditOutlined /> Chỉnh sửa dự án
          </Title>
          <Text type="secondary">
            Cập nhật thông tin {project?.projectName}
          </Text>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      width={900}
      footer={null}
      centered
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 30 }} />

      <Form form={form} layout="vertical">
        <div hidden={currentStep !== 0}>
          <Card>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  name="projectName"
                  label="Tên dự án"
                  rules={[{ required: true, message: "Nhập tên dự án" }]}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="project"
                  label="Mã dự án"
                  rules={[{ required: true, message: "Nhập mã dự án" }]}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="status" label="Trạng thái">
                  <Select
                    options={[
                      { value: ProjectStatus.NEW, label: "Mới" },
                      { value: ProjectStatus.IN_PROGRESS, label: "Đang làm" },
                      { value: ProjectStatus.DONE, label: "Hoàn thành" },
                      { value: ProjectStatus.CANCELLED, label: "Huỷ" },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="projectDescription"
                  label="Mô tả"
                  rules={[{ required: true, message: "Nhập mô tả" }]}
                >
                  <TextArea rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </div>

        <div hidden={currentStep !== 1}>
          <Card>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  name="customerName"
                  label="Khách hàng"
                  rules={[{ required: true, message: "Nhập khách hàng" }]}
                >
                  <Select
                    allowClear
                    listHeight={150}
                    showSearch
                    loading={isLoadingCustomer}
                    placeholder="Tìm kiếm và chọn khách hàng"
                    onSearch={debouncedCustomerSearch}
                    onClear={() => setCustomerSearch("")}
                    filterOption={false}
                    onPopupScroll={handleCustomerScroll}
                    options={
                      allCustomers?.map((customer) => ({
                        label: customer.name,
                        value: customer.name,
                      })) || []
                    }
                    notFoundContent={
                      isLoadingCustomer
                        ? "Đang tải..."
                        : customerSearch
                          ? "Không tìm thấy khách hàng"
                          : "Nhập để tìm kiếm"
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="contactProject"
                  label="Liên hệ"
                  rules={[{ required: true, message: "Chọn liên hệ" }]}
                >
                  <Select
                    allowClear
                    mode="multiple"
                    listHeight={150}
                    showSearch
                    loading={isLoadingContact}
                    placeholder="Tìm kiếm và chọn liên hệ"
                    onSearch={debouncedContactSearch}
                    onClear={() => setContactSearch("")}
                    onPopupScroll={handleContactScroll}
                    filterOption={false}
                    options={
                      allContacts?.map((contact) => ({
                        label: `${contact.name} - ${contact.phone} - ${contact.position}`,
                        value: `${contact.name} - ${contact.phone} - ${contact.position}`,
                      })) || []
                    }
                    notFoundContent={
                      isLoadingContact
                        ? "Đang tải..."
                        : contactSearch
                          ? "Không tìm thấy liên hệ"
                          : "Nhập để tìm kiếm"
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="requestFrom"
                  label="Nguồn yêu cầu"
                  rules={[{ required: true, message: "Nhập nguồn yêu cầu" }]}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </div>

        <div hidden={currentStep !== 2}>
          <Card>
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <Form.Item name="saleInCharge" label="Sale">
                  <Input size="large" prefix={<TeamOutlined />} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item name="saleAdInCharge" label="Sale Admin">
                  <Input size="large" prefix={<TeamOutlined />} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item name="purchaseInCharge" label="Mua hàng">
                  <Input size="large" prefix={<ShoppingOutlined />} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="requestDate"
                  label="Ngày yêu cầu"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="startDate"
                  label="Ngày bắt đầu"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="deadline"
                  label="Deadline"
                  rules={[{ required: true }]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </div>
      </Form>

      <div style={{ textAlign: "center", marginTop: 30 }}>
        <Space>
          {currentStep > 0 && <Button onClick={handlePrev}>Quay lại</Button>}
          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext}>
              Tiếp theo
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={updateProjectMutation.isPending}
              onClick={handleSubmit}
            >
              Cập nhật
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
};

export default ModalEditProductionProject;
